import { gtmPushData } from "@common/helpers/gtm";
import { PromoCardDetails } from "@modules/vehicle/types/vehiclePromotions";
import classNames from "classnames";
import { useState } from "react";
import ActionButton from "@modules/cms/enum/ActionButtonEnum";
import CmsButtonModalContainer from "@modules/cms/components/ButtonModalContainer";
import CloudflareImagesImage from "@common/components/CloudflareImagesImage";
import CmsButton from "@modules/cms/components/CmsButton";

type Props = {
  vehiclePromotion: PromoCardDetails;
};

const VehiclePromoCard = ({ vehiclePromotion }: Props) => {
  const { button } = vehiclePromotion;
  const promoWithImage =
    vehiclePromotion.backgroundImage || vehiclePromotion.mainCardImage;

  const [buttonModalState, setButtonModalState] = useState<{
    show: boolean;
    actionType: ActionButton;
  }>({ show: false, actionType: ActionButton.UnAssigned });

  return (
    <div
      className={classNames(
        "group rounded-lg shadow-elevation-00 overflow-hidden aspect-none pb-4 min-h-[24rem]",
        {
          "bg-white": promoWithImage,
          "bg-primary-light-400 bg-promo-swoop bg-no-repeat bg-[length:100%_100%]":
            !promoWithImage,
        }
      )}
    >
      <div className="items-center justify-center text-center h-full max-h-[24rem]">
        <div className="bg-primary-light">
          <div className="relative z-0">
            {vehiclePromotion.mainCardImage && (
              <div>
                <svg
                  fill="white"
                  className="w-full h-full absolute bg-primary-light -bottom-1 justify-center items-center z-10"
                  width="100%"
                  height="100%"
                >
                  <ellipse
                    overflow="hidden"
                    cx="50%"
                    cy="100%"
                    rx="60%"
                    ry="30%"
                  />
                </svg>
                <div className="flex flex-wrap justify-center">
                  <div className="w-2/5 px-4 pt-6 mb-2">
                    <CloudflareImagesImage
                      src={vehiclePromotion.mainCardImage.sourceUrl}
                      alt={vehiclePromotion.mainCardImage.altText}
                      width={200}
                      height={200}
                      className="z-20 shadow rounded-full max-w-full h-auto align-middle border-none object-cover relative"
                    />
                  </div>
                </div>
              </div>
            )}
            {!vehiclePromotion.mainCardImage &&
              vehiclePromotion.backgroundImage && (
                <>
                  <svg
                    fill="white"
                    className="w-full h-full absolute bg-transparent -bottom-1 justify-center items-center z-10"
                  >
                    <ellipse cx="50%" cy="100%" rx="60%" ry="20%" />
                  </svg>
                  <div className="group relative w-full h-40">
                    <CloudflareImagesImage
                      className="object-cover object-center"
                      src={vehiclePromotion.backgroundImage.sourceUrl}
                      alt={vehiclePromotion.backgroundImage.altText}
                      layout="fill"
                    />
                  </div>
                </>
              )}
          </div>
        </div>

        <div
          className={classNames("px-5 w-full flex flex-col", {
            "": promoWithImage,
            "h-full": !promoWithImage,
          })}
        >
          <div className="m-auto">
            {vehiclePromotion.cardHeading && (
              <div>
                <p className="font-semibold text-primary-bold text-2xl">
                  {vehiclePromotion.cardHeading}
                </p>
              </div>
            )}

            {vehiclePromotion.cardSubHeading && (
              <div className="pt-2">
                <p className="font-semibold text-primary-deep text-xl">
                  {vehiclePromotion.cardSubHeading}
                </p>
              </div>
            )}

            {vehiclePromotion.cardDescription && (
              <div className="pt-2">
                <p className="font-medium text-text-light-200">
                  {vehiclePromotion.cardDescription}
                </p>
              </div>
            )}

            {button && !button.isHidden && (
              <div className="pt-4">
                <CmsButton
                  className="caption-1 focus:outline-none focus-visible:shadow-elevation-03"
                  button={button}
                  setButtonModalState={setButtonModalState}
                  onClick={() => {
                    gtmPushData({
                      event: "Listing",
                      element: "CTA",
                      subElement: vehiclePromotion.cardHeading
                        ? vehiclePromotion.cardHeading
                        : "VLP Promo",
                      descriptor: button.isButtonLink
                        ? button.buttonLink.title
                        : button.actionButtonTitle,
                    });
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {buttonModalState.show && (
        <CmsButtonModalContainer buttonModalState={buttonModalState} />
      )}
    </div>
  );
};

export default VehiclePromoCard;
